import { Alert, Segmented, Spin } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { FormShell } from '@/form-renderer/FormShell';
import { useFormBranch } from '@/form-renderer/useFormBranch';
import {
  DevicePreviewFrame,
  DeviceViewportSwitcher,
  type DeviceKind,
} from '@/originator/DevicePreviewFrame';

type PreviewSide = 'previous' | 'pending';

type Props = {
  open: boolean;
  onCancel: () => void;
  /** Frozen pending-version template — the caller is expected to pass a
   *  template whose `.steps` is the version-under-review's snapshot, NOT the
   *  template's live state. `WorkflowReview` builds this by cloning
   *  `template` and swapping `.steps` with `latestVersion.steps`. */
  template: WorkflowTemplate;
  /** Optional frozen predecessor template — when present, the modal shows a
   *  "previous ↔ pending" version toggle so the approver can flip between
   *  what advisors see today (v1) and what they'll see if this pending
   *  version is approved. Absent on first submissions (no v1). */
  prevTemplate?: WorkflowTemplate;
  /** Label rendered on the version toggle for the pending side (e.g. "v1.1"). */
  nextLabel: string;
  /** Label rendered on the version toggle for the previous side (e.g. "v1").
   *  Ignored when `prevTemplate` is absent. */
  prevLabel?: string;
  /** Optional step id to land on when the preview opens — lets the caller
   *  deep-link to a specific diff row's target step. */
  initialStepId?: string;
};

/**
 * AC2 on "Preview and reject all workflow changes at once": lets the approver
 * preview the workflow (in all viewport modes) from within the review page
 * without navigating away. Wraps the same `FormShell` + `DevicePreviewFrame`
 * the standalone `/preview` page uses, inside a full-viewport `<BmoModal>`
 * so the diff panel underneath keeps its full width when the preview is
 * closed.
 *
 * `mode="preview"` on FormShell — advisor input state is throwaway, no data
 * saves, no advisor-side effects fire. Same shell the eventual advisor route
 * will use so what an approver sees in preview matches what the advisor
 * will experience once that route ships.
 */
export function InlineWorkflowPreviewModal({
  open,
  onCancel,
  template,
  prevTemplate,
  nextLabel,
  prevLabel,
  initialStepId,
}: Props) {
  // The form's own language is resolved inside `BranchablePreview`, which is the component
  // that renders it — this one only labels the modal chrome.
  const { t } = useTranslation();
  // Local viewport state — mirrors PreviewPage. Resets on modal close so a
  // subsequent open lands back on Desktop by default.
  const [device, setDevice] = useState<DeviceKind>('desktop');
  // Which frozen snapshot to render — pending is the default (that's what
  // the approver is reviewing). Only the pending option shows when the
  // workflow has no predecessor (first submission).
  const [side, setSide] = useState<PreviewSide>('pending');
  const hasPrev = Boolean(prevTemplate);
  const activeTemplate = side === 'previous' && prevTemplate ? prevTemplate : template;

  return (
    <BmoModal
      open={open}
      onCancel={() => {
        setDevice('desktop');
        setSide('pending');
        onCancel();
      }}
      // Fullscreen: 100vw wide, pinned to `top: 0` so it sits flush with the
      // viewport, and content flex-columned to the viewport height so the body
      // region owns the vertical space. The body is `overflow: auto` so a long
      // form scrolls INSIDE the modal instead of pushing the chrome off-screen —
      // Preview must always give the approver the header + close affordance,
      // no matter how long the workflow.
      //
      // `dvh`, not `vh`: this is an inline height, so it outranks the phone band
      // in global.css that sizes every modal to `100dvh`. Left as `vh` the
      // content box would stand taller than the sheet around it by exactly the
      // height of a mobile browser's URL bar, hiding the foot of the form.
      width="100vw"
      style={{ top: 0, paddingBottom: 0, maxWidth: '100vw', margin: 0 }}
      contentStyle={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}
      title={t('approver.review.previewModal.title', { defaultValue: 'Preview' })}
      bodyStyle={{
        padding: '16px 20px 20px',
        background: '#f6f7f8',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 12,
          flexShrink: 0,
        }}
      >
        {hasPrev ? (
          <Segmented<PreviewSide>
            value={side}
            onChange={setSide}
            aria-label={t('approver.review.previewModal.versionSwitcher', {
              defaultValue: 'Version',
            })}
            options={[
              {
                value: 'previous',
                label: t('approver.review.previewModal.previousOption', {
                  defaultValue: '{{label}} (live)',
                  label: prevLabel ?? '',
                }),
              },
              {
                value: 'pending',
                label: t('approver.review.previewModal.pendingOption', {
                  defaultValue: '{{label}} (pending)',
                  label: nextLabel,
                }),
              },
            ]}
          />
        ) : (
          // Preserve the toggle's slot so Desktop / Tablet / Mobile stays
          // right-aligned regardless of whether the version toggle is shown.
          <div />
        )}
        <DeviceViewportSwitcher device={device} onChange={setDevice} />
      </div>
      <DevicePreviewFrame device={device}>
        {/* key={side} — force a remount when the approver flips versions. Without it,
            per-step state (activeStepId, input values) leaks from the pending render into
            the previous one and vice versa, which reads as "the toggle didn't flip". The
            branch state has to reset with it, which is why `useFormBranch` lives inside
            this component rather than in the modal: a branch selected while reviewing the
            pending version must not still be composed in on the previous one. */}
        <BranchablePreview key={side} template={activeTemplate} initialStepId={initialStepId} />
      </DevicePreviewFrame>
    </BmoModal>
  );
}

/**
 * One version's preview, with form branching wired.
 *
 * Split out solely to carry the `key={side}` remount described at the call site — the hook's
 * state is per-version and has to die with it.
 */
function BranchablePreview({
  template,
  initialStepId,
}: {
  template: WorkflowTemplate;
  initialStepId?: string;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const {
    effectiveTemplate,
    formBranch,
    onFormBranchChange,
    loading: branchLoading,
    loadFailed: branchLoadFailed,
  } = useFormBranch({ template, loadTemplate: workflowsApi.get });

  return (
    <>
      {branchLoading && (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          style={{ marginBottom: 12 }}
          message={t('preview.branch.loading')}
        />
      )}
      {branchLoadFailed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('preview.branch.unavailable')}
        />
      )}
      {/* The approver is reviewing a FROZEN snapshot of this workflow, but nothing records
          which version of the branch target that snapshot was authored against — so the
          branched half resolves to the target's current published version. Worth saying
          plainly on a surface whose entire premise is "this is exactly what you are
          approving": half of what is on screen is not part of it. */}
      {formBranch && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('preview.branch.notVersionPinned')}
        />
      )}
      <FormShell
        // The COMPOSED template once a branch is active — never a different `key` here. The
        // version-flip remount is owned one level up, on this component.
        template={effectiveTemplate ?? template}
        formBranch={formBranch}
        onFormBranchChange={onFormBranchChange}
        language={language}
        mode="preview"
        initialStepId={initialStepId}
        // No `onOptionRedirect` on purpose, so a redirecting answer shows no
        // modal here. This preview lives INSIDE the review modal, and the
        // only honest thing a redirect could do is navigate away — which
        // would abandon the review the approver is in the middle of. The
        // full-page preview (PreviewPage, reachable from the same review
        // screen) is where the redirect can be exercised for real.
        //
        // Branching is different and IS wired: it swaps the form in place, so it never
        // navigates away from the review and can be exercised right here.
      />
    </>
  );
}
